import { useEffect, useRef, useState } from 'react';
import { useLobby, NeedsMe, GameAddress } from '../utils/Lobby';
import { SwitchUser } from '../utils/Identity';
import { Post } from '../utils/RemoteAuthority';
import './Lobby.css';

// The bar along the top of every page, games included: the way home and to the other pages, what is waiting on us, and who we are (signing out; with the development tools, being someone else). extra: anything the page
// adds for testing (a game's "viewing as"), shown with the development tools only. page: which page this is ('games',
// 'stats', 'cards', 'admin', 'profile', or none).
function TopBar({ page, extra })
{
    const { me, games, users } = useLobby();
    const waiting = NeedsMe(games);
    const link = (name, href, label) => <a className={`tta-topbar-link${page === name ? ' on' : ''}`} href={href}>{label}</a>;
    return <>
        <header className="tta-topbar">
            <a className="tta-topbar-brand" href="./">
                <span className="tta-topbar-mark">◆</span>Through the Ages
                {me?.devTools && <span className="tta-dev-badge" title="Development: test games, viewing as anyone, playing as anyone">dev</span>}
            </a>
            <nav className="tta-topbar-nav">
                {link('games', './', 'Games')}
                {link('stats', './?stats', 'Stats')}
                {link('cards', './?cards', 'Cards')}
                {me && <NextGame games={games}/>}
            </nav>
            <div className="tta-topbar-spacer"/>
            {me?.devTools && extra && <div className="tta-topbar-extra">{extra}</div>}
            {me && <>
                <Waiting games={waiting}/>
                <Profile me={me} users={users}/>
            </>}
        </header>
    </>;
}

// A menu that opens under its button and closes on a click anywhere else
function Menu({ button, className, title, children })
{
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    return <div className="tta-menu" ref={ref}>
        <button className={className} title={title} onClick={() => setOpen(!open)}>{button}</button>
        {open && <div className="tta-menu-panel">{children}</div>}
    </div>;
}

// The way to the next game waiting for a move of ours (our turn or a decision), with how many there are. Not shown on a
// game that is waiting for us itself (the move to make is here), nor when no other game is. From a game, "next" is the
// first waiting game after it, going round to the start.
function NextGame({ games })
{
    const here = Number(new URLSearchParams(window.location.search).get('game'));
    const waiting = (games ?? []).filter(game => game.you && game.status?.state !== 'over');
    // (the server's ids are strings)
    if (waiting.some(game => Number(game.id) === here))
    {
        return null;
    }
    const next = waiting.find(game => Number(game.id) > here) ?? waiting[0];
    if (!next)
    {
        return null;
    }
    return <a className="tta-topbar-next" href={GameAddress(next)}
        title={`${next.title}: ${next.you === 'turn' ? 'your turn' : 'your decision'}`}>
        Next game<span className="tta-topbar-count">{waiting.length}</span>
    </a>;
}

// The games waiting on us: how many, and each one
function Waiting({ games })
{
    const reason = (game) => game.you === 'turn' ? 'Your turn' : game.you === 'decision' ? 'Your decision' : `New game from ${game.createdBy ?? 'someone'}`;
    return <Menu className={`tta-topbar-bell${games.length > 0 ? ' on' : ''}`}
        title={games.length > 0 ? `${games.length} game${games.length === 1 ? '' : 's'} waiting on you` : 'Nothing waiting on you'}
        button={<>⚑<span className="tta-topbar-count">{games.length}</span></>}>
        <div className="tta-menu-title">Waiting on you</div>
        {games.length === 0 && <div className="tta-menu-empty">Nothing right now.</div>}
        {games.map(game =>
            <a key={game.id} className="tta-menu-item" href={GameAddress(game)}>
                <span className="tta-menu-item-title">{game.title}</span>
                <span className={`tta-menu-item-note${game.isNew && !game.you ? ' new' : ''}`}>{reason(game)}</span>
            </a>)}
    </Menu>;
}

// Who we are: signing out (with a password), or, with the development tools, picking who to be (the admin among them);
// for admins, the admin page
function Profile({ me, users })
{
    const signOut = async () => {
        await Post('/auth/password/logout').catch(() => {});
        window.location.href = './';
    };
    return <Menu className="tta-topbar-profile" title={`Signed in as ${me.login}`}
        button={<><span className="tta-avatar">{me.name.slice(0, 1).toUpperCase()}</span><span className="tta-topbar-name">{me.name}</span>▾</>}>
        <div className="tta-menu-title">{me.name}<div className="tta-muted">{me.login}</div></div>
        {me.switchable && <label className="tta-menu-dev">
            <span className="tta-dev-badge">dev</span> Play as
            <select className="tta-input" value={me.login} onChange={(e) => SwitchUser(e.target.value)}>
                {/* (everyone, the admin too; we are among them) */}
                {(me.switchTo ?? users).map(user => <option key={user.login} value={user.login}>{user.name} ({user.login}){user.admin ? ', admin' : ''}</option>)}
            </select>
        </label>}
        <a className="tta-menu-item" href="./?profile">Profile</a>
        <a className="tta-menu-item" href="./?stats">Your stats</a>
        {me.admin && <a className="tta-menu-item" href="./?admin">Admin</a>}
        {me.form && <button className="tta-menu-item" onClick={signOut}>Sign out</button>}
        {/* (which version of the site this is, from the image's build: none when run with npm) */}
        {process.env.REACT_APP_VERSION && <div className="tta-menu-version">Version {process.env.REACT_APP_VERSION}</div>}
    </Menu>;
}

export default TopBar;
